import { smallerClosingCross } from "@/designSystemIcons";
import { BREAKPOINTS } from "@/settings";
import clsx from "clsx";
import { useState } from "react";
import { useMediaQuery } from "usehooks-ts";

import "./input-no-spinner.css";

export type BasicCustomFilterProps = {
  label?: string;
  minValue: number;
  maxValue: number;
  onChangeMin: (value: number) => void;
  onChangeMax: (value: number) => void;
  isTitle?: boolean;
  minPlaceHolder?: number;
  maxPlaceHolder?: number;
  large?: boolean;
  slim?: boolean;
  isOnSbcFilterSidebar?: boolean;
};

// Basic custom filter component, for when the filter has a min and max value
export const RangeFilter = ({
  label,
  minValue,
  maxValue,
  onChangeMin,
  onChangeMax,
  isTitle,
  minPlaceHolder,
  maxPlaceHolder,
  large,
  slim = false,
  isOnSbcFilterSidebar,
}: BasicCustomFilterProps) => {
  const [isMinFocused, setMinFocused] = useState(false);
  const [isMaxFocused, setMaxFocused] = useState(false);

  return (
    <div
      className={clsx(
        "flex justify-between group",
        isOnSbcFilterSidebar ? "hover-none" : "hover:bg-white/5",
        !slim && " p-1",
        isTitle ? "border-b-2 border-white/20 rounded-t-md" : "rounded-md",
      )}
    >
      <span
        className={clsx(
          "my-auto font-medium",
          isTitle
            ? "text-sm font-bold text-secondary-dark"
            : "text-xs text-gray-300",
        )}
      >
        {label}
      </span>
      <div className="flex gap-x-1">
        {!isOnSbcFilterSidebar && (minValue || maxValue) && (
          <button
            className="flex desktop:hidden desktop:group-hover:flex items-center justify-center w-4 h-4 my-auto rounded-full bg-primary-red fill-white"
            onClick={(e) => {
              onChangeMin(null);
              onChangeMax(null);
              e.stopPropagation();
            }}
          >
            {smallerClosingCross}
          </button>
        )}
        <InputField
          onChange={onChangeMin}
          onFocus={() => setMinFocused(true)}
          onBlur={(val) => {
            const max = maxPlaceHolder || 99;
            if (maxValue && val > maxValue) onChangeMax(val > max ? max : val);
            setMinFocused(false);
          }}
          value={minValue}
          placeHolder={isMinFocused ? "" : minPlaceHolder?.toString() || "1"}
          isFocused={isMinFocused}
          min={minPlaceHolder}
          max={maxPlaceHolder}
          large={large}
          isOnSbcFilterSidebar={isOnSbcFilterSidebar}
        />
        <InputField
          onChange={onChangeMax}
          onFocus={() => setMaxFocused(true)}
          onBlur={(val) => {
            const min = minPlaceHolder || 1;
            if (minValue && val < minValue) onChangeMin(val < min ? min : val);
            setMaxFocused(false);
          }}
          value={maxValue}
          placeHolder={isMaxFocused ? "" : maxPlaceHolder?.toString() || "99"}
          isFocused={isMaxFocused}
          max={maxPlaceHolder}
          min={minPlaceHolder}
          large={large}
          isOnSbcFilterSidebar={isOnSbcFilterSidebar}
        />
      </div>
    </div>
  );
};

type InputFieldProps = {
  onChange: (value: number) => void;
  onFocus: () => void;
  onBlur: (val: number) => void;
  value: number;
  placeHolder: string;
  isFocused: boolean;
  min?: number;
  max?: number;
  large?: boolean;
  isOnSbcFilterSidebar?: boolean;
};

const InputField = ({
  onChange,
  onFocus,
  onBlur,
  value,
  placeHolder,
  isFocused,
  min,
  max,
  large,
  isOnSbcFilterSidebar,
}: InputFieldProps) => {
  const isMobile = useMediaQuery(BREAKPOINTS.MOBILE.query);
  return (
    <input
      className={clsx(
        !isOnSbcFilterSidebar &&
          "rounded-sm text-center text-white outline-hidden border-[1px] focus:border-primary-dark-blue caret-primary-dark-blue",
        !isOnSbcFilterSidebar &&
          (value && !isFocused
            ? "bg-primary-dark-blue border-primary-dark-blue hover:bg-primary-washed-blue hover:border-primary-washed-blue font-medium"
            : "border-black/0 bg-black/30 hover:bg-black/75"),
        !isOnSbcFilterSidebar && (large ? "w-[55px]" : "w-[35px]"),
        isOnSbcFilterSidebar &&
          clsx(
            "w-17.5 h-6 text-center outline-hidden rounded-sm",
            isFocused
              ? "bg-new-gray-825"
              : "bg-panel-background hover:bg-new-gray-700",
          ),
        isOnSbcFilterSidebar && isMobile && clsx("w-17.5 h-9"),
      )}
      placeholder={placeHolder}
      type="number"
      min={min || 1}
      max={max || 99}
      onChange={(e) => {
        onChange(parseInt(e.target.value) || null);
      }}
      onFocus={onFocus}
      onBlur={(e) => {
        max = max || 99;
        min = min || 1;
        if (parseInt(e.target.value) < min) onChange(min);
        else if (parseInt(e.target.value) > max) onChange(max);
        onBlur(parseInt(e.target.value));
      }}
      value={value || ""}
      // onKeyDown={(e) => e.key === "Escape" && e.currentTarget.blur()}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          e.currentTarget.blur();
        }
        if (e.key === "Escape") {
          e.currentTarget.blur();
        }
      }}
    />
  );
};
